import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  Select,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiSend } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { useAllUsers } from '../../hooks/useAdmin';

export default function AdminEmail() {
  const [searchParams] = useSearchParams();
  const { users, loading: usersLoading } = useAllUsers();
  const toast = useToast();

  const [userId, setUserId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  // Pre-fill from URL params (e.g., from AdminUserDetail)
  useEffect(() => {
    const paramUserId = searchParams.get('userId');
    if (paramUserId) {
      setUserId(paramUserId);
    }
  }, [searchParams]);

  const handleSend = async () => {
    if (!userId || !subject.trim() || !body.trim()) return;
    setSending(true);

    try {
      const adminSendEmail = httpsCallable(functions, 'adminSendEmail');
      await adminSendEmail({ userId, subject: subject.trim(), body: body.trim() });
      toast({
        title: 'Email sent',
        description: 'The email has been sent successfully.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      setSubject('');
      setBody('');
    } catch (err) {
      toast({
        title: 'Error sending email',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  const selectedUser = users.find((u) => u.id === userId);

  return (
    <VStack spacing={6} align="stretch">
      <Box>
        <Heading size="lg">Send Email</Heading>
        <Text color="gray.500" mt={1}>Compose and send an email to a user</Text>
      </Box>

      <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} p={6} maxW="600px">
        <VStack spacing={5} align="stretch">
          <FormControl isRequired>
            <FormLabel>Recipient</FormLabel>
            <Select
              placeholder="Select user..."
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              isDisabled={usersLoading}
            >
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.anonymizedUsername} ({u.email})
                </option>
              ))}
            </Select>
            {selectedUser && (
              <Text fontSize="xs" color="gray.500" mt={1}>
                Email will be sent to: {selectedUser.email}
              </Text>
            )}
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Subject</FormLabel>
            <Input
              placeholder="Email subject..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Message</FormLabel>
            <Textarea
              placeholder="Email body (plain text)..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
            />
          </FormControl>

          <Button
            colorScheme="orange"
            leftIcon={<Icon as={FiSend} />}
            onClick={handleSend}
            isLoading={sending}
            loadingText="Sending..."
            isDisabled={!userId || !subject.trim() || !body.trim()}
            alignSelf="flex-end"
          >
            Send Email
          </Button>
        </VStack>
      </Box>
    </VStack>
  );
}
